import Vue from 'vue';
import VueRouter from 'vue-router';
import designSystemRoutes from 'design-system-routes';
import store from '../store';

// Layouts
import AuthLayout from '../layouts/AuthLayout.vue';
import DashboardLayout from '../layouts/DashboardLayout.vue';
import PublicLayout from '../layouts/PublicLayout.vue';

// Pages
import Login from '../views/Login.vue';
import Dashboard from '../views/Dashboard.vue';
import Agents from '../views/Agents.vue';
import AgentDetail from '../views/AgentDetail.vue';
import CallHistory from '../views/CallHistory.vue';
import CallDetail from '../views/CallDetail.vue';
import AgentPage from '../views/AgentPage.vue';
import Signup from '../views/Signup.vue';
import ForgotPassword from '../views/ForgotPassword.vue';
import ResetPassword from '../views/ResetPassword.vue';
import Settings from '../views/Settings.vue';
import ApiTokens from '../views/ApiTokens.vue';
import CreateApiToken from '../views/CreateApiToken.vue';
import TTS from '../views/TTS.vue';
import STT from '../views/STT.vue';
import AgentModels from '../views/AgentModels.vue';
import AgentModelForm from '../views/AgentModelForm.vue';
import AgentConfigs from '../views/AgentConfigs.vue';
import AgentConfigForm from '../views/AgentConfigForm.vue';
import AgentConfigDetail from '../views/AgentConfigDetail.vue';
import Users from '../views/Users.vue';
import Roles from '../views/Roles.vue';
import Permissions from '../views/Permissions.vue';
import SipTrunk from '../views/SipTrunk.vue';
import OutboundCampaigns from '../views/OutboundCampaigns.vue';
import OutboundCampaignDetail from '../views/OutboundCampaignDetail.vue';
import AgentWebPage from '../views/AgentWebPage.vue';
import NotFound from '../views/NotFound.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: Login,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/signup',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'Signup',
        component: Signup,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/forgot-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/reset-password',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: { requiresGuest: true },
      },
    ],
  },
  {
    path: '/agent/:id/web',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'AgentWebPage',
        component: AgentWebPage,
        props: true,
      },
    ],
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/agent-models',
        name: 'AgentModels',
        component: AgentModels,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/agent-models/new',
        name: 'CreateAgentModel',
        component: AgentModelForm,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/agent-models/:id',
        name: 'EditAgentModel',
        component: AgentModelForm,
        props: true,
        meta: { requiresSuperuser: true },
      },
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard,
      },
      {
        path: '/agents',
        name: 'Agents',
        component: Agents,
      },
      {
        path: '/agent-config',
        name: 'AgentConfigs',
        component: AgentConfigs,
      },
      {
        path: '/agent-config/create',
        name: 'CreateAgentConfig',
        component: AgentConfigForm,
      },
      {
        path: '/agent-config/:externalId',
        name: 'AgentConfigDetail',
        component: AgentConfigDetail,
        props: true,
      },
      {
        path: '/agent-config/:externalId/edit',
        name: 'EditAgentConfig',
        component: AgentConfigForm,
        props: true,
      },
      {
        path: '/agents/:id',
        name: 'AgentDetail',
        component: AgentDetail,
        props: true,
      },
      {
        path: '/call-history',
        name: 'CallHistory',
        component: CallHistory,
        meta: { requiresAuth: true, requiredPermission: 'read:call-message' },
      },
      {
        path: '/call-history/:id',
        name: 'CallDetail',
        component: CallDetail,
        meta: { requiresAuth: true, requiredPermission: 'read:call-message' },
      },
      {
        path: '/sip-trunk',
        name: 'SipTrunk',
        component: SipTrunk,
        meta: { requiresSuperuser: true },
      },
      {
        path: '/outbound-campaigns',
        name: 'OutboundCampaigns',
        component: OutboundCampaigns,
        meta: { requiresAuth: true, requiredPermission: 'read:outbound-campaign' },
      },
      {
        path: '/outbound-campaigns/:id',
        name: 'OutboundCampaignDetail',
        component: OutboundCampaignDetail,
        meta: { requiresAuth: true, requiredPermission: 'read:outbound-campaign' },
      },
      {
        path: '/api-tokens',
        name: 'ApiTokens',
        component: ApiTokens,
      },
      {
        path: '/api-tokens/create',
        name: 'CreateApiToken',
        component: CreateApiToken,
      },
      {
        path: '/tts',
        name: 'TTS',
        component: TTS,
      },
      {
        path: '/stt',
        name: 'STT',
        component: STT,
      },
      {
        path: '/settings',
        name: 'Settings',
        component: Settings,
      },
      {
        path: '/users',
        name: 'Users',
        component: Users,
        meta: { requiresAuth: true, requiredPermission: 'read:user' },
      },
      {
        path: '/roles',
        name: 'Roles',
        component: Roles,
        meta: { requiresAuth: true, requiredPermission: 'read:role' },
      },
      {
        path: '/permissions',
        name: 'Permissions',
        component: Permissions,
        meta: { requiresAuth: true, requiredPermission: 'read:permission' },
      },
      ...designSystemRoutes,
      {
        path: '/test-agent/:id',
        name: 'TestAgent',
        component: AgentPage,
      },
      {
        path: '/create-agent',
        name: 'CreateAgent',
        component: AgentPage,
      },
      {
        path: '/edit-agent/:id',
        name: 'EditAgent',
        component: AgentPage,
      },
    ],
  },
  {
    path: '*',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'NotFound',
        component: NotFound,
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// Navigation Guards
router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const user = store.getters['auth/user'];

  // Authentication gerektiren sayfalar
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated || !user) {
      next('/login');
      return;
    }
    // RBAC: sadece superuser'lara açık bir sayfa mı?
    if (to.matched.some((record) => record.meta.requiresSuperuser) && !store.getters['auth/isSuperuser']) {
      store.dispatch('ui/showNotification', {
        type: 'error',
        message: 'You do not have permission to access this page.',
      }, { root: true });
      next('/dashboard');
      return;
    }
    // RBAC: sayfanın gerektirdiği izin var mı?
    const requiredPermission = to.matched
      .map((record) => record.meta.requiredPermission)
      .find(Boolean);
    if (requiredPermission && !store.getters['auth/hasPermission'](requiredPermission)) {
      store.dispatch('ui/showNotification', {
        type: 'error',
        message: 'You do not have permission to access this page.',
      }, { root: true });
      next('/dashboard');
      return;
    }
    next();
  } else if (to.matched.some((record) => record.meta.requiresGuest)) {
    // Guest kullanıcılar için sayfalar (login sayfası gibi)
    if (isAuthenticated && user) {
      next('/dashboard');
      return;
    }
    next();
  } else {
    // Diğer sayfalar
    next();
  }
});

export default router;
